import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * Gets the section IDs to fetch for soft navigation.
 * @returns {string[]} Array of section IDs.
 */
const getProductSections = () => {
  const sections = [];
  const mainSection = document.querySelector('[id^="shopify-section-"][id*="__main"], #shopify-section-main');
  if (mainSection) {
    const sectionId = mainSection.id.replace('shopify-section-', '');
    sections.push(sectionId);
  } else {
    sections.push('main');
  }
  const recommendationsSection = document.querySelector('[id^="shopify-section-"][id*="recommendations"]');
  if (recommendationsSection) {
    const sectionId = recommendationsSection.id.replace('shopify-section-', '');
    sections.push(sectionId);
  }
  return sections;
};

/**
 * Preload an image into the browser cache.
 */
const preloadImage = (url, options = {}) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  if (options.srcset) link.setAttribute('imagesrcset', options.srcset);
  if (options.sizes) link.setAttribute('imagesizes', options.sizes);
  if (options.fetchpriority) link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
};

/**
 * Prefetch Manager - caches product sections
 */
class VariationPrefetchManager {
  #cache = new Map();
  #maxCacheSize = 20;
  #ttl = 60_000;

  prefetch(url) {
    if (!url) return undefined;
    const cached = this.#cache.get(url);
    const now = Date.now();
    if (cached) {
      if (now - cached.timestamp < this.#ttl) return cached.promise;
      this.#cache.set(url, { ...cached, timestamp: now });
      this.#fetchAndCache(url);
      return cached.promise;
    }
    return this.#fetchAndCache(url);
  }

  async get(url, signal) {
    const cached = this.#cache.get(url);
    if (cached) return cached.promise;
    return this.#fetchAndCache(url, signal);
  }

  #fetchAndCache(url, signal) {
    const sectionUrl = this.#buildSectionUrl(url);
    const fetchPromise = fetch(sectionUrl, {
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        this.#cache.delete(url);
        throw error;
      });
    this.#cache.set(url, { promise: fetchPromise, timestamp: Date.now() });
    this.#trimCache();
    return fetchPromise;
  }

  #buildSectionUrl(url) {
    const [baseUrl, query] = url.split('?');
    const sections = getProductSections().join(',');
    const params = new URLSearchParams(query || '');
    const variantId = params.get('variant');
    return variantId
      ? `${baseUrl}?sections=${sections}&variant=${encodeURIComponent(variantId)}`
      : `${baseUrl}?sections=${sections}`;
  }

  #trimCache() {
    if (this.#cache.size <= this.#maxCacheSize) return;
    const keysToDelete = Array.from(this.#cache.keys()).slice(0, this.#cache.size - this.#maxCacheSize);
    for (const key of keysToDelete) {
      this.#cache.delete(key);
    }
  }
}

const prefetchManager = new VariationPrefetchManager();

/**
 * Product variations component
 * @extends {Component}
 */
export class ProductVariations extends Component {
  #isLoading = false;
  #loadingTimer = null;
  #currentFetchController = null;
  #popstateHandler = null;

  connectedCallback() {
    super.connectedCallback();

    this.swatchesContainer = this.querySelector('.product-card__swatches');
    if (!this.swatchesContainer) return;

    // Detect context
    const isProductPage =
      document.body.classList.contains('template-product') || window.location.pathname.includes('/products/');
    const productCard = this.closest('product-card') || this.closest('.resource-card');

    if (productCard && !isProductPage) {
      // Collection page - simple mode
      this.#initializeSimpleMode();
      return;
    }

    // Product page - soft navigation
    this.#initializeNavigateMode();
    this.#initializePopstateHandler();
  }

  #initializeSimpleMode() {
    if (!this.swatchesContainer) return;
    const thumbnails = this.swatchesContainer.querySelectorAll('.product-card__thumbnail');
    if (thumbnails.length === 0) return;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      thumbnail.addEventListener('click', () => {
        const url = thumbnail.dataset.variationUrl;
        if (url && !thumbnail.classList.contains('is-active')) {
          window.location.href = url;
        }
      });
    });
  }

  #initializeNavigateMode() {
    if (!this.swatchesContainer) return;
    const thumbnails = this.swatchesContainer.querySelectorAll('.product-card__thumbnail');
    if (thumbnails.length === 0) return;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      // Prefetch on hover
      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        const url = thumbnail.dataset.variationUrl;
        if (url) prefetchManager.prefetch(url)?.catch(() => {});
        const image = thumbnail.dataset.variationImage;
        if (image) {
          const srcset = thumbnail.dataset.variationImageSrcset || undefined;
          const galleryImg = this.closest('[id^="shopify-section"]')?.querySelector(
            'media-gallery img.product-media__image',
          );
          const rawSizes = galleryImg?.sizes;
          const sizes = rawSizes?.replace(/^\s*auto\s*,\s*/i, '') || undefined;
          preloadImage(image, { srcset, sizes, fetchpriority: true });
        }
      };

      thumbnail.addEventListener('mouseenter', warm);
      thumbnail.addEventListener('pointerdown', warm);

      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#handleVariationClick(thumbnail);
      });
    });
  }

  #initializePopstateHandler() {
    this.#popstateHandler = this.#handlePopstate.bind(this);
    if (this.#popstateHandler) {
      window.addEventListener('popstate', this.#popstateHandler);
    }
  }

  async #handlePopstate(event) {
    if (event.state?.softNav || document.location.pathname.includes('/products/')) {
      if (this.#currentFetchController) this.#currentFetchController.abort();
      this.#currentFetchController = new AbortController();
      try {
        await this.#updatePageContent(document.location.href, this.#currentFetchController.signal);
      } finally {
        this.#currentFetchController = null;
      }
    }
  }

  async #handleVariationClick(thumbnail) {
    if (thumbnail.classList.contains('is-active') || this.#isLoading) return;
    const variationUrl = thumbnail.dataset.variationUrl;
    if (!variationUrl) return;

    if (this.swatchesContainer) {
      this.swatchesContainer
        .querySelectorAll('.product-card__thumbnail')
        .forEach((t) => t.classList.remove('is-active'));
    }
    thumbnail.classList.add('is-active');

    await this.#softNavigate(variationUrl);
  }

  async #softNavigate(url) {
    if (this.#currentFetchController) this.#currentFetchController.abort();
    this.#currentFetchController = new AbortController();

    try {
      await this.#updatePageContent(url, this.#currentFetchController.signal);
      history.pushState({ softNav: true }, '', url);
    } finally {
      this.#currentFetchController = null;
    }
  }

  async #updatePageContent(url, signal) {
    this.#isLoading = true;
    this.#setLoadingState(true);

    try {
      const sections = await prefetchManager.get(url, signal);
      if (!sections || typeof sections !== 'object') {
        throw new Error('Invalid sections response');
      }

      const parsedSections = Object.entries(sections)
        .filter(([, html]) => html && typeof html === 'string')
        .map(([sectionId, html]) => ({
          sectionId,
          doc: new DOMParser().parseFromString(html, 'text/html'),
        }));

      const doUpdate = () => {
        for (const { sectionId, doc } of parsedSections) {
          this.#updateSectionFromDoc(sectionId, doc);
        }

        const firstDoc = parsedSections[0]?.doc;
        if (firstDoc) {
          const h1 = firstDoc.querySelector('h1');
          if (h1) document.title = `${h1.textContent?.trim() ?? ''} – ${window.Shopify?.shop ?? ''}`;
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      };

      if (typeof document.startViewTransition === 'function') {
        await document.startViewTransition(doUpdate).finished;
      } else {
        doUpdate();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      this.#isLoading = false;
      this.#setLoadingState(false);
    }
  }

  #updateSectionFromDoc(sectionId, doc) {
    let currentSection = document.querySelector(`#shopify-section-${sectionId}`);
    if (!currentSection) {
      currentSection = document.querySelector(`[id^="shopify-section-"][id$="__${sectionId}"]`);
    }
    if (!currentSection) return;

    const newSectionWrapper = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
    if (!newSectionWrapper) return;

    for (const link of newSectionWrapper.querySelectorAll('link[rel="prefetch"], link[rel="preload"]')) {
      link.remove();
    }

    for (const img of newSectionWrapper.querySelectorAll('.product-card__thumbnail img')) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'sync');
    }

    const currentId = currentSection.id;
    const currentClasses = currentSection.className;

    currentSection.innerHTML = newSectionWrapper.innerHTML;
    currentSection.id = currentId;
    currentSection.className = currentClasses;

    this.#reinitializeExternalScripts(currentSection);
    currentSection.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
  }

  #reinitializeExternalScripts(container) {
    const scripts = container.querySelectorAll('script[src]');
    for (const oldScript of scripts) {
      if (!(oldScript instanceof HTMLScriptElement)) continue;
      const newScript = document.createElement('script');
      newScript.type = oldScript.type || 'module';
      newScript.src = oldScript.src;
      oldScript.replaceWith(newScript);
    }
  }

  #setLoadingState(loading) {
    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (loading) {
      this.#loadingTimer = setTimeout(() => {
        this.#loadingTimer = null;
        this.classList.add('is-loading');
        const productInfo = document.querySelector('#shopify-section-main');
        if (productInfo) {
          productInfo.classList.add('is-loading');
          productInfo.setAttribute('aria-busy', 'true');
        }
      }, 400);
    } else {
      this.classList.remove('is-loading');
      const productInfo = document.querySelector('#shopify-section-main');
      if (productInfo) {
        productInfo.classList.remove('is-loading');
        productInfo.setAttribute('aria-busy', 'false');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (this.#popstateHandler) {
      window.removeEventListener('popstate', this.#popstateHandler);
      this.#popstateHandler = null;
    }

    if (this.#currentFetchController) {
      this.#currentFetchController.abort();
      this.#currentFetchController = null;
    }
  }
}

if (!customElements.get('product-variations')) {
  customElements.define('product-variations', ProductVariations);
}
